import { Flex, Image } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useRef, useEffect } from "react";

import {
  ActivePlaylistClipState,
  FallbackPlaylistTypeState,
  WatchedClipsCountState,
} from "MVVM/State/embeddablePlaylist";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { formatPlaylistClip } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import {
  PlaylistClip,
  PlaylistClips,
} from "MVVM/Views/EmbeddablePlaylist/@types";

const Thumbnail = styled(Image)<{
  $active: boolean;
}>`
  aspect-ratio: 16/9;
  border: ${({ $active }) => {
    if (!$active) return "1.5px solid inherit";
    return "1.5px solid #E2263C";
  }};
  border-radius: 4px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.4);
  flex: 0 0 auto;
  height: 100%;

  object-fit: cover;
`;

const StyledThumbnailContainer = styled(Flex)`
  -ms-overflow-style: none;
  align-items: stretch;
  cursor: pointer;
  flex-wrap: nowrap;
  gap: 6px;
  height: 100%;
  overflow-x: scroll;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ThumbnailRow = ({
  clips,
  onWatchLimitReached,
}: {
  clips: PlaylistClips;
  onWatchLimitReached?: (playerName: string, clickedClip: PlaylistClip) => void;
}) => {
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );
  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState(
    WatchedClipsCountState,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const setFallbackPlaylistType = useSetRecoilState(FallbackPlaylistTypeState);
  const { track } = useAnalyticsContext();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      const isAtLeftEdge = container.scrollLeft === 0;
      const isAtRightEdge =
        container.scrollLeft + container.clientWidth >= container.scrollWidth;

      // Only prevent parent scroll if we can scroll horizontally
      if ((e.deltaY > 0 && !isAtRightEdge) || (e.deltaY < 0 && !isAtLeftEdge)) {
        e.preventDefault();
        e.stopPropagation();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!clips) return <></>;

  const handleThumbnailClick = (thumbnail: PlaylistClip) => {
    if (watchedClipsCount >= 2 && onWatchLimitReached) {
      const playerName = thumbnail.username;
      onWatchLimitReached(playerName || thumbnail.username || "", thumbnail);

      return;
    }
    const formattedClip = formatPlaylistClip(thumbnail);

    track("Button - Click", {
      locationIdentifier: "thumbnail_click",
      location: "embeddable_playlist",
    });
    setFallbackPlaylistType("");
    setActivePlaylistClip(formattedClip);
    setWatchedClipsCount((prev) => prev + 1);
  };

  return (
    <StyledThumbnailContainer ref={containerRef}>
      {clips.map((clip: PlaylistClip) => (
        <Thumbnail
          key={clip._id}
          src={clip.thumbnailOverlayUrl || clip.thumbnailStandardUrl}
          onClick={() => handleThumbnailClick(clip)}
          $active={
            activePlaylistClip
              ? activePlaylistClip.internalId === clip.internalId
              : false
          }
        />
      ))}
    </StyledThumbnailContainer>
  );
};
